export const SNAPSHOT_FULL_WIDTH_KEYS = new Set([
  'content_summary', 'remark', 'description', 'handover', 'reason',
  'performance_address', 'change_reason', 'business_scope', 'borrow_reason',
  'dereg_reason', 'approval_attachment_paths', 'approval_files', 'scan_file',
  'attachment_paths', 'expense_lines_summary', 'prepayment_lines_summary',
  'subject'
]);

export const SNAPSHOT_TEXTAREA_KEYS = new Set([
  'content_summary', 'remark', 'description', 'handover', 'reason',
  'performance_address', 'change_reason', 'business_scope', 'borrow_reason',
  'dereg_reason', 'expense_lines_summary', 'prepayment_lines_summary'
]);

export const SNAPSHOT_FILE_LIST_KEYS = new Set([
  'attachment_paths', 'approval_attachment_paths', 'approval_files', 'scan_file',
  'other_attachments', 'no_invoice_commitment_files'
]);

export const SNAPSHOT_FILE_LIST_LABELS = new Set([
  '附件', '文件', '其他附件', '无票承诺书'
]);

/** 快照中需格式化为金额的 number 字段（附件张数等计数类字段不在此列） */
export const SNAPSHOT_CURRENCY_KEYS = new Set([
  'invoice_total', 'actual_pay_total', 'prepay_total', 'amount', 'total_amount', 'unit_price'
]);

export function isSnapshotCurrencyField(item) {
  const key = resolveSnapshotItemKey(item);
  if (key && SNAPSHOT_CURRENCY_KEYS.has(key)) return true;
  const label = item?.label || '';
  return /金额|总额|实付|预付|单价/.test(label) && !/张数|数量/.test(label);
}

export const SNAPSHOT_TEXTAREA_LABELS = new Set([
  '补偿事由', '离职原因', '调岗原因', '交接说明', '说明', '备注', '内容摘要',
  '申请原因', '注销原因', '变更原因', '事由', '报销明细', '预付明细', '主题'
]);

/**
 * 解析快照字段展示值。
 * @param {object} item - form_display 项（含 key / label / value）
 * @param {object} formData - 流程 form_data
 * @param {{ preferFormData?: boolean }} options
 *   preferFormData=true（默认）：退回重提等场景优先用 form_data 最新值；
 *   preferFormData=false：只读详情优先用快照 item.value（已格式化展示，避免 ID/对象覆盖）。
 */
export function resolveSnapshotFieldValue(item, formData, { preferFormData = true } = {}) {
  if (!preferFormData) {
    if (item?.value != null && item.value !== '') return item.value;
    if (item?.value === 0) return item.value;
  }

  const key = resolveSnapshotItemKey(item);
  if (key && formData && Object.prototype.hasOwnProperty.call(formData, key)) {
    const live = formData[key];
    if (live != null && typeof live === 'object') {
      return item?.value ?? '';
    }
    if (live !== undefined && live !== null && live !== '') return live;
    if (live === 0 || live === '') return live;
  }
  return item?.value ?? '';
}

export function resolveSnapshotItemKey(item) {
  if (item?.key) return item.key;
  const labelKeyMap = {
    附件: 'attachment_paths',
    补偿事由: 'reason',
    离职原因: 'reason',
    调岗原因: 'reason',
    交接说明: 'handover',
    说明: 'description',
    备注: 'remark',
    内容摘要: 'content_summary',
    申请原因: 'borrow_reason',
    注销原因: 'dereg_reason',
    变更原因: 'reason',
    事由: 'reason',
    报销明细: 'expense_lines_summary',
    预付明细: 'prepayment_lines_summary',
    主题: 'subject',
    其他附件: 'other_attachments',
    无票承诺书: 'no_invoice_commitment_files'
  };
  return labelKeyMap[item?.label] || null;
}

export function parseSnapshotFileList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function resolveSnapshotFiles(item, formData) {
  if (Array.isArray(item?.files) && item.files.length) return item.files;
  const key = resolveSnapshotItemKey(item);
  const raw = key ? formData?.[key] : null;
  return parseSnapshotFileList(raw);
}

export function isSnapshotFullWidthItem(item) {
  if (item?.full_width) return true;
  const key = resolveSnapshotItemKey(item);
  if (key && SNAPSHOT_FULL_WIDTH_KEYS.has(key)) return true;
  if (item?.type === 'textarea' || item?.type === 'file_list') return true;
  if (SNAPSHOT_FILE_LIST_LABELS.has(item?.label)) return true;
  if (SNAPSHOT_TEXTAREA_LABELS.has(item?.label)) return true;
  return String(item?.value || '').length > 60;
}

export function isSnapshotTextareaItem(item, valueStr) {
  if (item?.type === 'textarea') return true;
  const key = resolveSnapshotItemKey(item);
  if (key && SNAPSHOT_TEXTAREA_KEYS.has(key)) return true;
  if (SNAPSHOT_TEXTAREA_LABELS.has(item?.label)) return true;
  return valueStr.includes('\n');
}

export function isSnapshotFileListItem(item) {
  if (item?.type === 'file_list') return true;
  const key = resolveSnapshotItemKey(item);
  if (key && SNAPSHOT_FILE_LIST_KEYS.has(key)) return true;
  if (SNAPSHOT_FILE_LIST_LABELS.has(item?.label)) return true;
  return Array.isArray(item?.files) && item.files.length > 0;
}
